import { ThemeEvents } from '@theme/events';

/**
 * Automatic gift add/remove.
 *
 * snippets/cart-products.liquid checks whether settings.auto_gift_trigger_product
 * (or a product from settings.auto_gift_trigger_collection) is in the cart and
 * whether the tagged gift line (properties._auto_gift) matches that, exposing
 * data-auto-gift-action="add:<variantId>" or "remove:<key>" on .cart-items__wrapper.
 * Acting on it here triggers a cart:update, which re-renders every
 * cart-items-component and re-evaluates the same check against the now-current
 * cart — so it self-corrects (add when the trigger appears, remove again once
 * it's gone) without this file knowing anything about products or collections.
 *
 * A customer can remove the gift line by hand via the normal remove/quantity
 * controls; assets/component-cart-items.js detects the data-auto-gift marker
 * on that row and sets the _auto_gift_removed cart attribute in the same
 * request, so it isn't immediately re-added here while the trigger is still
 * in the cart.
 */

const ACTION_SELECTOR = '[data-auto-gift-action]';

// Guards against acting twice at once when several cart:update events land
// close together (e.g. this file's own dispatch re-entering synchronously).
// The next real cart:update re-checks the actual state, so nothing is missed.
let pending = false;

/**
 * @param {ParentNode} root
 * @returns {string}
 */
function readAction(root) {
  const el = root.querySelector(ACTION_SELECTOR);
  return el ? el.getAttribute('data-auto-gift-action') || '' : '';
}

/**
 * @param {Record<string, string> | undefined} sections
 * @returns {string | null} the action found in any section carrying the marker, or null if none of the given sections carry it
 */
function readActionFromSections(sections) {
  if (!sections) return null;

  for (const html of Object.values(sections)) {
    if (typeof html !== 'string' || html.indexOf('data-auto-gift-action') === -1) continue;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    return readAction(doc);
  }

  return null;
}

function cartSectionIds() {
  /** @type {string[]} */
  const ids = [];

  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.push(item.dataset.sectionId);
    }
  });

  return ids;
}

/**
 * @param {string} url
 * @param {object} body
 */
function postJSON(url, body) {
  return fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((response) => response.json());
}

async function fetchCartSections() {
  const sectionIds = cartSectionIds();
  if (sectionIds.length === 0) return undefined;

  const response = await fetch(`/?sections=${sectionIds.join(',')}`);
  return response.json();
}

/**
 * @param {Record<string, string> | undefined} sections
 * @param {number} [itemCount]
 */
function dispatchCartUpdate(sections, itemCount) {
  document.dispatchEvent(
    new CustomEvent(ThemeEvents.cartUpdate, {
      bubbles: true,
      detail: {
        data: {
          source: 'cart-auto-gift',
          itemCount,
          sections,
        },
      },
    })
  );
}

/**
 * @param {string} action
 */
async function processAction(action) {
  if (pending || !action) return;
  pending = true;

  try {
    if (action.indexOf('add:') === 0) {
      const variantId = action.slice(4);
      const addData = await postJSON('/cart/add.js', {
        items: [{ id: variantId, quantity: 1, properties: { _auto_gift: 'true' } }],
      });
      if (addData.status) return; // Shopify error responses carry a `status` field

      const sections = await fetchCartSections();
      dispatchCartUpdate(sections);
    } else if (action.indexOf('remove:') === 0) {
      const key = action.slice(7);
      const changeData = await postJSON('/cart/change.js', { id: key, quantity: 0 });
      if (changeData.status) return;

      const sections = await fetchCartSections();
      dispatchCartUpdate(sections, changeData.item_count);

      // A customer removing the auto-gift line by hand (see
      // assets/component-cart-items.js) sets _auto_gift_removed so it isn't
      // forced back in while the trigger is still there. That flag otherwise
      // lives on the cart forever, including once the cart is emptied out
      // entirely — so a later, unrelated shopping session on the same
      // browser would never get the gift again either. Clear it once the
      // cart is genuinely empty, so a fresh trigger gets a fresh shot at it.
      if (changeData.item_count === 0 && changeData.attributes && changeData.attributes._auto_gift_removed) {
        await postJSON('/cart/update.js', { attributes: { _auto_gift_removed: '' } });
      }
    }
  } finally {
    pending = false;
  }
}

/**
 * @param {Record<string, string> | undefined} sections
 */
function checkAndProcess(sections) {
  const action = sections ? readActionFromSections(sections) : readAction(document);
  if (action) processAction(action);
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const sections = /** @type {CustomEvent} */ (event).detail?.data?.sections;
  checkAndProcess(sections);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => checkAndProcess(undefined), { once: true });
} else {
  checkAndProcess(undefined);
}
